import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/api';
import UserSearchDropdown from './UserSearchDropdown';
import RichTextEditor from '../common/RichTextEditor';
import { stripHTML } from '../../utils/htmlSanitizer';
import './SendOrganizationalCoin.css';

/**
 * SendOrganizationalCoin Component
 * Allows organization admins to send organizational coins on behalf of the organization
 *
 * @param {Object} props
 * @param {Object} props.organization - The organization object (must have user_id and display_name)
 * @param {Function} props.onSuccess - Optional callback after successful coin send
 */
function SendOrganizationalCoin({ organization, onSuccess }) {
  const auth = useAuth();
  const [selectedReceiver, setSelectedReceiver] = useState(null);
  const [coinMessage, setCoinMessage] = useState('');
  const [sendingCoin, setSendingCoin] = useState(false);
  const [sendCoinError, setSendCoinError] = useState(null);
  const [sendCoinSuccess, setSendCoinSuccess] = useState(false);

  const handleSendCoin = async (e) => {
    e.preventDefault();

    if (!selectedReceiver) {
      setSendCoinError('Please select a receiver');
      return;
    }

    const plainText = stripHTML(coinMessage).trim();
    if (!plainText) {
      setSendCoinError('Please enter a message');
      return;
    }

    if (plainText.length > 1000) {
      setSendCoinError('Message must be 1000 characters or less');
      return;
    }

    setSendingCoin(true);
    setSendCoinError(null);

    try {
      const idToken = auth.user.id_token;

      // Create a coin with organizational type and the organization as the issuer
      const newCoin = await apiService.createCoin(idToken, 'organizational', coinMessage.trim(), organization.user_id);

      // Send it from the organization to the receiver
      await apiService.sendCoin(idToken, newCoin.coin_id, selectedReceiver.user_id, organization.user_id);

      setSendCoinSuccess(true);

      // Call onSuccess callback if provided
      if (onSuccess) {
        await onSuccess();
      }

      // Reset form after 2 seconds
      setTimeout(() => {
        setSendCoinSuccess(false);
        setSelectedReceiver(null);
        setCoinMessage('');
      }, 2000);
    } catch (err) {
      console.error('Error sending coin:', err);
      setSendCoinError(err.message);
    } finally {
      setSendingCoin(false);
    }
  };

  return (
    <section className="send-organizational-coin-section">
      <h2>Send Organizational Coin</h2>
      {sendCoinSuccess ? (
        <div className="success-banner">
          <span className="success-icon">✓</span>
          <p>Coin sent successfully from {organization.display_name}!</p>
        </div>
      ) : (
        <form onSubmit={handleSendCoin} className="send-org-coin-form">
          <div className="form-group">
            <label htmlFor="receiver">Receiver *</label>
            <UserSearchDropdown
              onSelect={(user) => {
                setSelectedReceiver(user);
                setSendCoinError(null);
              }}
              placeholder="Search by name or email..."
              debounceMs={500}
              searchFn={async (query) => {
                const idToken = auth.user.id_token;
                return await apiService.searchUsers(idToken, query, 10);
              }}
              disabled={sendingCoin}
            />
            {selectedReceiver && (
              <div className="selected-user-info">
                <small>
                  Selected: <strong>{selectedReceiver.display_name}</strong> ({selectedReceiver.email})
                </small>
              </div>
            )}
            <small>Search and select the person to receive this organizational coin.</small>
          </div>

          <div className="form-group">
            <label htmlFor="message">Message *</label>
            <RichTextEditor
              value={coinMessage}
              onChange={setCoinMessage}
              placeholder="Write a message to accompany your coin..."
              disabled={sendingCoin}
              maxLength={10000}
            />
          </div>

          {sendCoinError && (
            <div className="error-message">
              {sendCoinError}
            </div>
          )}

          <button
            type="submit"
            className="submit-button"
            disabled={sendingCoin}
          >
            {sendingCoin ? 'Sending...' : 'Send Organizational Coin'}
          </button>
        </form>
      )}
    </section>
  );
}

export default SendOrganizationalCoin;
